// frontend/src/components/common/Header.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { 
  Menu, 
  X, 
  User, 
  Bell,
  LogOut,
  Shield,
  Stethoscope,
  Building2,
  Pill,
  Car,
  Users,
  Check,
  Trash2
} from 'lucide-react';
import { useNotificationContext } from '../../context/NotificationContext';
import { notificationService } from '../../services/notificationService';
import { formatDistanceToNow } from 'date-fns';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const { user, logout } = useAuth();
  const { unreadCount, fetchUnreadCount } = useNotificationContext();
  const location = useLocation();
  const navigate = useNavigate();
  
  const notificationRef = useRef(null);
  const profileRef = useRef(null);

  const navigation = [
    { name: 'Home', href: '/', public: true },
    { name: 'Find Hospitals', href: '/hospitals', public: true },
    { name: 'Find Pharmacies', href: '/pharmacies', public: true },
    { name: 'Emergency', href: '/emergency', public: true },
    { name: 'About', href: '/about', public: true },
    { name: 'Contact', href: '/contact', public: true },
  ];

  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setIsNotificationOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  
  useEffect(() => {
    if (isNotificationOpen && user) {
      fetchNotifications();
    }
  }, [isNotificationOpen, user]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const result = await notificationService.getNotifications({ limit: 10 });
      if (result.success) {
        setNotifications(result.data?.notifications || []);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      const result = await notificationService.markAsRead(notificationId);
      if (result.success) {
        setNotifications(prev =>
          prev.map(n => n._id === notificationId ? { ...n, isRead: true } : n)
        );
        fetchUnreadCount();
      }
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      const result = await notificationService.deleteNotification(notificationId);
      if (result.success) {
        setNotifications(prev => prev.filter(n => n._id !== notificationId));
        fetchUnreadCount();
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.isRead) {
      handleMarkAsRead(notification._id);
    }
    
   
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
      setIsNotificationOpen(false);
    } else if (notification.relatedEntity?.entityType) {
      const linkMap = {
        Consultation: `/consultations/${notification.relatedEntity.entityId}`,
        Order: `/orders/${notification.relatedEntity.entityId}`,
        Appointment: `/appointments/${notification.relatedEntity.entityId}`,
        Prescription: `/prescriptions/${notification.relatedEntity.entityId}`,
        Ambulance: `/ambulance/${notification.relatedEntity.entityId}`,
      };
      
      const link = linkMap[notification.relatedEntity.entityType];
      if (link) {
        navigate(link);
        setIsNotificationOpen(false);
      }
    }
  };

  const getRoleIcon = (role) => {
    const iconClass = "w-4 h-4";
    switch (role) {
      case 'admin': 
      case 'superadmin':
        return <Shield className={iconClass} />;
      case 'doctor': 
        return <Stethoscope className={iconClass} />;
      case 'hospital_admin':
      case 'hospital_staff':
      case 'hospital':
        return <Building2 className={iconClass} />;
      case 'pharmacy':
      case 'pharmacy_admin':
        return <Pill className={iconClass} />;
      case 'ambulance':
      case 'ambulance_staff':
        return <Car className={iconClass} />;
      default: 
        return <Users className={iconClass} />;
    }
  };

  const getDashboardRoute = (role) => {
    const routes = {
      admin: '/admin/dashboard',
      superadmin: '/admin/dashboard',
      patient: '/patient',
      doctor: '/doctor',
      hospital_admin: '/hospital',
      hospital_staff: '/hospital',
      hospital: '/hospital',
      pharmacy: '/pharmacy',
      pharmacy_admin: '/pharmacy',
      ambulance: '/ambulance'
    };
    return routes[role] || '/patient';
  };

  const getProfileRoute = (role) => {
    const routes = {
      admin: '/admin/profile',
      superadmin: '/admin/profile',
      patient: '/patient/profile',
      doctor: '/doctor/profile',
      hospital_admin: '/hospital/profile',
      hospital_staff: '/hospital/profile',
      hospital: '/hospital/profile',
      pharmacy: '/pharmacy/profile',
      pharmacy_admin: '/pharmacy/profile',
      driver: '/driver/profile',
      ambulance_driver: '/driver/profile'
    };
    return routes[role] || '/patient/profile';
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsProfileOpen(false);
  };

  const handleProfileClick = () => {
    setIsProfileOpen(!isProfileOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const closeProfile = () => {
    setIsProfileOpen(false);
  };

  return (
    <header className="bg-white shadow-lg sticky top-0 z-50">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-3">
              <div className="relative">
                <img 
                  className="h-12 w-12 rounded-full object-cover border-2 border-primary-200 shadow-sm" 
                  src="/images/logo.png" 
                  alt="MediGo" 
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="hidden h-12 w-12 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 items-center justify-center border-2 border-primary-200 shadow-sm">
                  <span className="text-white font-bold text-xl">M</span>
                </div>
              </div>
              <span className="text-2xl font-bold text-primary-600">MediGo</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                  location.pathname === item.href
                    ? 'text-primary-600 bg-primary-50'
                    : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* User Actions */}
          <div className="flex items-center space-x-4">
            {user ? (
              <>
                {/* Notifications Dropdown */}
                <div className="relative" ref={notificationRef}>
                  <button 
                    onClick={() => setIsNotificationOpen(!isNotificationOpen)}
                    className="p-2 text-gray-400 hover:text-gray-600 relative transition-colors duration-200 rounded-full hover:bg-gray-100"
                    aria-label="Notifications"
                  >
                    <Bell className="w-6 h-6" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full min-w-[20px] h-5 flex items-center justify-center text-xs font-bold px-1 animate-pulse">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>

                  {/* Notification Dropdown */}
                  {isNotificationOpen && (
                    <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-[600px] flex flex-col">
                      {/* Header */}
                      <div className="flex items-center justify-between p-4 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900">
                          Notifications
                          {unreadCount > 0 && (
                            <span className="ml-2 text-sm font-normal text-gray-500">
                              ({unreadCount} new)
                            </span>
                          )}
                        </h3>
                        <button
                          onClick={() => setIsNotificationOpen(false)}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>

                      {/* Notifications List */}
                      <div className="flex-1 overflow-y-auto">
                        {loading ? (
                          <div className="p-8 text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="text-sm text-gray-500 mt-2">Loading...</p>
                          </div>
                        ) : notifications.length === 0 ? (
                          <div className="p-8 text-center">
                            <Bell className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                            <p className="text-sm text-gray-600 font-medium">No notifications</p>
                            <p className="text-xs text-gray-500 mt-1">You're all caught up!</p>
                          </div>
                        ) : (
                          <div className="divide-y divide-gray-100">
                            {notifications.map((notification) => (
                              <div
                                key={notification._id}
                                className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                                  !notification.isRead ? 'bg-blue-50' : ''
                                }`}
                                onClick={() => handleNotificationClick(notification)}
                              >
                                <div className="flex items-start gap-3">
                                  {/* Icon */}
                                  <span className="text-2xl flex-shrink-0">
                                    {notificationService.getNotificationIcon(notification.type)}
                                  </span>

                                  {/* Content */}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 line-clamp-2 mb-1">
                                      {notification.title}
                                    </p>
                                    <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                                      {notification.message}
                                    </p>
                                    
                                    <div className="flex items-center justify-between">
                                      <p className="text-xs text-gray-500">
                                        {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                                      </p>
                                      
                                      {notification.priority === 'urgent' && (
                                        <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full font-medium">
                                          Urgent
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  {/* Actions */}
                                  <div className="flex items-start gap-1 flex-shrink-0">
                                    {!notification.isRead && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleMarkAsRead(notification._id);
                                        }}
                                        className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-100 rounded transition-colors"
                                        title="Mark as read"
                                      >
                                        <Check className="w-4 h-4" />
                                      </button>
                                    )}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteNotification(notification._id);
                                      }}
                                      className="p-1 text-red-600 hover:text-red-700 hover:bg-red-100 rounded transition-colors"
                                      title="Delete"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Footer */}
                      {notifications.length > 0 && (
                        <div className="p-3 border-t border-gray-200 bg-gray-50">
                          <Link
                            to="/shared/notifications"
                            className="block text-center text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
                            onClick={() => setIsNotificationOpen(false)}
                          >
                            View all notifications
                          </Link>
                        </div>
                      )}
                    </div>
                  )}
                </div>


                {/* Profile Dropdown */}
                <div className="relative" ref={profileRef}>
                  <button
                    onClick={handleProfileClick}
                    className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-50 transition-colors duration-200"
                    aria-label="User profile menu"
                  >
                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                      {getRoleIcon(user.role)}
                    </div>
                    <div className="hidden sm:block text-left">
                      <div className="text-sm font-medium text-gray-700">
                        {user.firstName} {user.lastName}
                      </div>
                      <div className="text-xs text-gray-500 capitalize">
                        {user.role?.replace('_', ' ')}
                      </div>
                    </div>
                  </button>

                  {isProfileOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                      <Link
                        to={getDashboardRoute(user.role)}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                        onClick={closeProfile}
                      >
                        <User className="w-4 h-4 mr-3" />
                        Dashboard
                      </Link>
                      <Link
                        to={getProfileRoute(user.role)}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                        onClick={closeProfile}
                      >
                        <User className="w-4 h-4 mr-3" />
                        Profile
                      </Link>
                      <Link
                        to="/shared/notifications"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                        onClick={closeProfile}
                      >
                        <Bell className="w-4 h-4 mr-3" />
                        All Notifications
                      </Link>
                      
                      <hr className="my-1" />
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50 transition-colors duration-200"
                      >
                        <LogOut className="w-4 h-4 mr-3" />
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center space-x-4">
                <Link
                  to="/auth/login"
                  className="text-gray-700 hover:text-primary-600 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200"
                >
                  Login
                </Link>
                <Link
                  to="/auth/register"
                  className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200"
                >
                  Register
                </Link>
              </div>
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 transition-colors duration-200"
              aria-label="Toggle mobile menu"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 border-t border-gray-200">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${
                    location.pathname === item.href
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                  }`}
                  onClick={closeMenu}
                >
                  {item.name}
                </Link>
              ))}
              
              {user && (
                <Link
                  to="/notifications"
                  className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 transition-colors duration-200 flex items-center"
                  onClick={closeMenu}
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Notifications
                  {unreadCount > 0 && (
                    <span className="ml-2 bg-red-500 text-white rounded-full px-2 py-0.5 text-xs">
                      {unreadCount}
                    </span>
                  )}
                </Link>
              )}
              
              {!user && (
                <>
                  <Link
                    to="/auth/login"
                    className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 transition-colors duration-200"
                    onClick={closeMenu}
                  >
                    Login
                  </Link>
                  <Link
                    to="/auth/register"
                    className="block px-3 py-2 rounded-md text-base font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors duration-200"
                    onClick={closeMenu}
                  >
                    Register
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;